import {
  Background,
  Direction,
  Displaying,
  dontForwardTheseProps,
  Greyx,
  ProfileCompletionLevel,
  ProfileCover,
  ProfileSections,
  Spacing,
  Typography,
} from "@bonusx-global/bx-design-system";
import { styled, useEventCallback } from "@mui/material";
import type { BoxProps } from "@mui/material/Box";
import Box from "@mui/material/Box";
import { enqueueSnackbar } from "notistack";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileQuery } from "../../../../api/features/profile/use-profile.query";
import { ProfileCompletionLevelExplanation } from "../../../../features/profile/components/ProfilePage/profile-completion-level-explaination/profile-completion-level-explanation";
import { useProfileSections } from "../../../../features/profile/hooks/use-profile-sections/use-profile-sections";
import useIsMobile from "../../../../hooks/device/useIsMobile";
import { Loading } from "../../../../legacy/components/Loading";
import { useCurrentUserSubscriptionTier } from "../../../../legacy/hooks/use-current-user-subscription-tier/use-current-user-subscription-tier";
import { useAccount } from "../../../../legacy/hooks/useAccount";
import type { IWithIsMobile } from "../../../../legacy/interfaces/IWithIsMobile.interface";
import { uploadNewProfileImageGeneric } from "../../../../legacy/pages/ProfilePage/utils/profile-page-functions";
import type { BONUSX_SUBSCRIPTION_TIERS } from "../../../../legacy/services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { BONUSX_SUBSCRIPTION_TIER_TO_GENERAL_SUBSCRIPTION_TIER_MAP } from "../../../../legacy/services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { backendMediaUrlParser } from "../../../../legacy/utils";
import { isValidImageFile } from "../../../../legacy/utils/document-structure";
import { formatDate } from "../../../../legacy/utils/format-date";

const PROFILE_MAX_WIDTH = "62.125rem";

interface ProfileComponentSizes {
  profileCompletionLevel: "medium" | "large";
  profileCover: "small" | "large";
  sectionSubtitle: "body1" | "body2";
  sectionTitle: "h6" | "subtitle1";
}

const ProfilePageContainer = styled(Box, {
  shouldForwardProp: dontForwardTheseProps<IWithIsMobile, BoxProps>(["isMobile"]),
})<IWithIsMobile>(({ isMobile }) => ({
  width: "100%",
  maxWidth: PROFILE_MAX_WIDTH,
  margin: "0 auto",
  backgroundColor: Background.default,
  minHeight: "100vh",
  paddingTop: isMobile ? Spacing["1500"] : Spacing["2500"],
  paddingBottom: isMobile ? Spacing["5000"] : Spacing["1500"],
  display: Displaying.flex,
  flexDirection: Direction.column,
  gap: isMobile ? Spacing["1500"] : Spacing["2000"],
}));

const ProfileCoverContainer = styled(Box)({
  width: "100%",
  backgroundColor: Greyx["0"],
});

const ProfileContentContainer = styled(Box, {
  shouldForwardProp: dontForwardTheseProps<IWithIsMobile, BoxProps>(["isMobile"]),
})<IWithIsMobile>(({ isMobile }) => ({
  display: Displaying.flex,
  flexDirection: Direction.column,
  gap: isMobile ? Spacing["1500"] : Spacing["2000"],
}));

const ProfileCompletionLevelWrapper = styled(Box)({
  cursor: "pointer",
  padding: `${Spacing["500"]} 0`,
});

const YOUR_INFORMATION_TITLE = "Le tue informazioni";
const YOUR_INFORMATION_SUBTITLE =
  "Ci servono per verificare i requisiti per le pratiche e mostrarti i bonus a cui hai diritto";
const MOBILE_COMPONENT_SIZES: Readonly<ProfileComponentSizes> = {
  profileCompletionLevel: "medium",
  profileCover: "small",
  sectionSubtitle: "body2",
  sectionTitle: "subtitle1",
};

const DESKTOP_COMPONENT_SIZES: Readonly<ProfileComponentSizes> = {
  sectionSubtitle: "body1",
  sectionTitle: "h6",
  profileCompletionLevel: "large",
  profileCover: "large",
};

export default function ProfilePage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { profile, sectionsById, loading: profileLoading, error: currentProfileError } = useProfileQuery();
  const { currentUserTier } = useCurrentUserSubscriptionTier();
  const currentUserTierAsBonusxSubscriptionTier =
    BONUSX_SUBSCRIPTION_TIER_TO_GENERAL_SUBSCRIPTION_TIER_MAP[currentUserTier as BONUSX_SUBSCRIPTION_TIERS];
  const { accountApi, accountData, revalidate: revalidateAccountData } = useAccount();
  const profileImage = backendMediaUrlParser(accountData?.pictureUrl);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const profileName = `${profile?.firstName}`;
  const profileUpdatedAt = profile?.lastUpdatedDate;
  const activeStep = profile?.completion?.activeStep ?? 0;

  const [isExplanationOpen, setIsExplanationOpen] = useState(false);

  const handleFileInputChange = useEventCallback(function handleFileInputChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const files = event.target.files;
    const imageFile = files?.[0];

    if (!imageFile) return;

    if (!isValidImageFile(imageFile)) {
      enqueueSnackbar("Il file selezionato non è un'immagine valida.", { variant: "error" });
      return;
    }

    uploadNewProfileImageGeneric({
      imageFile,
      revalidateAccountData,
      showSuccessSnackbar: enqueueSnackbar,
      showErrorSnackbar: enqueueSnackbar,
      accountApi,
    });
  });

  const onProfileIconClick = useEventCallback(function onProfileIconClick(): void {
    fileInputRef.current?.click();
  });

  const handleProfileCompletionLevelClick = useCallback(function handleProfileCompletionLevelClick(): void {
    setIsExplanationOpen(true);
  }, []);

  const handleExplanationClose = useCallback(function handleExplanationClose(): void {
    setIsExplanationOpen(false);
  }, []);

  const profileSections = useProfileSections({
    navigate,
    sectionsById,
  });

  const componentSizes = isMobile ? MOBILE_COMPONENT_SIZES : DESKTOP_COMPONENT_SIZES;

  if (profileLoading) {
    return <Loading />;
  }

  if (currentProfileError) {
    // TODO: handle this error
    return <></>;
  }

  return (
    <ProfilePageContainer isMobile={isMobile}>
      <ProfileCoverContainer>
        <input hidden ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} />
        <ProfileCover
          profileName={profileName}
          profileUpdatedAt={formatDate(profileUpdatedAt) ?? ""}
          profileImage={profileImage}
          subscriptionTier={currentUserTierAsBonusxSubscriptionTier}
          size={componentSizes.profileCover}
          sx={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          onProfileIconClick={onProfileIconClick}
        />
      </ProfileCoverContainer>

      <ProfileContentContainer isMobile={isMobile}>
        <ProfileCompletionLevelWrapper onClick={handleProfileCompletionLevelClick}>
          <ProfileCompletionLevel size={componentSizes.profileCompletionLevel} activeStep={activeStep} />
        </ProfileCompletionLevelWrapper>
      </ProfileContentContainer>

      <ProfileCompletionLevelExplanation
        open={isExplanationOpen}
        onClose={handleExplanationClose}
        isMobile={isMobile}
        activeStep={activeStep}
      />

      <ProfileContentContainer isMobile={isMobile}>
        <Box>
          <Typography variant={componentSizes.sectionTitle}>{YOUR_INFORMATION_TITLE}</Typography>
          <Typography variant={componentSizes.sectionSubtitle}>{YOUR_INFORMATION_SUBTITLE}</Typography>
        </Box>
        <ProfileSections items={profileSections} />
      </ProfileContentContainer>
    </ProfilePageContainer>
  );
}
